// src/lib/time/api-client.ts
import { z } from "zod";

const errorBody = z.object({ error: z.string() });

/** A failed call to our own API; `message` is the server's text, safe to show. */
export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function toApiError(res: Response): Promise<ApiError> {
  const json: unknown = await res.json().catch(() => null);
  const parsed = errorBody.safeParse(json);
  return new ApiError(
    res.status,
    parsed.success ? parsed.data.error : "Something went wrong. Try again.",
  );
}

export async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { accept: "application/json" } });
  if (!res.ok) throw await toApiError(res);
  const body: T = await res.json();
  return body;
}

export async function sendJson<T>(method: "POST" | "PUT", url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await toApiError(res);
  const json: T = await res.json();
  return json;
}

/** A DELETE to our own API; the body, if any, is ignored. */
export async function deleteJson(url: string): Promise<void> {
  const res = await fetch(url, { method: "DELETE", headers: { accept: "application/json" } });
  if (!res.ok) throw await toApiError(res);
}
